import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import { Calendar, Clock, User, Phone, Mail, MapPin, CheckCircle, Car } from 'lucide-react';
import NavBar from '../components/NavBar';
import { notifyNewOrder } from '../services/notificationService';
import SmartImage from '../components/SmartImage';

const ModificationBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [modification, setModification] = useState(null);
  const [formData, setFormData] = useState({
    name: currentUser?.displayName || '',
    phone: '',
    email: currentUser?.email || '',
    address: '',
    carModel: '',
    carYear: '',
    carType: '',
    bookingDate: '',
    bookingTime: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState('modifications');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    loadModification();
  }, [id, currentUser, navigate]);

  // التنقل عند تغيير الصفحة من NavBar
  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    } else if (currentPage === 'services') {
      navigate('/services');
    } else if (currentPage === 'maintenance') {
      navigate('/maintenance');
    } else if (currentPage === 'accessories') {
      navigate('/accessories');
    } else if (currentPage === 'parts') {
      navigate('/parts');
    }
  }, [currentPage, navigate]);

  const loadModification = async () => {
    try {
      const modDoc = await getDoc(doc(db, COLLECTIONS.MODIFICATIONS, id));
      if (modDoc.exists()) {
        setModification({ id: modDoc.id, ...modDoc.data() });
      }
    } catch (error) {
      console.error('Error loading modification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const bookingData = {
        type: 'modification-consultation',
        modificationName: modification.name,
        modificationId: modification.id,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerEmail: formData.email,
        customerAddress: formData.address,
        carModel: formData.carModel,
        carYear: formData.carYear,
        carType: formData.carType,
        bookingDate: formData.bookingDate,
        bookingTime: formData.bookingTime,
        notes: formData.notes,
        status: 'pending',
        createdAt: Timestamp.now()
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.ORDERS), bookingData);
      await notifyNewOrder({ ...bookingData, id: docRef.id, total: 0 });
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (error) {
      console.error('Error submitting booking:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NavBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
        </div>
      </div>
    );
  }

  if (!modification) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          {success ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">تم حجز الاستشارة بنجاح!</h2>
              <p className="text-gray-600 mb-2">سيتم التواصل معك قريباً لتأكيد موعد الاستشارة</p>
              <p className="text-sm text-gray-500">فريقنا المتخصص سيراجع طلبك ويقدم لك أفضل الحلول</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="bg-gradient-to-r from-red-500 to-orange-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center gap-4">
                  <SmartImage
                    src={modification.icon}
                    alt={modification.name}
                    size="large"
                    fallback="⚙️"
                  />
                  <div>
                    <h2 className="text-3xl font-bold">{String(modification.name || '')}</h2>
                    <p className="text-lg opacity-90">{String(modification.price || '')}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-blue-50 border-r-4 border-blue-500 p-4 mb-6 rounded-lg">
                  <p className="text-blue-800 text-sm text-right">
                    <strong>ملاحظة:</strong> هذه استشارة مجانية لفهم احتياجات سيارتك وتقديم أفضل حلول التعديل المناسبة
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                      <User className="w-4 h-4" />
                      الاسم الكامل
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    العنوان
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>

                {/* معلومات السيارة */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 text-right flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    معلومات السيارة
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                        نوع السيارة
                      </label>
                      <input
                        type="text"
                        placeholder="مثال: مرسيدس، BMW"
                        value={formData.carType}
                        onChange={(e) => setFormData({ ...formData, carType: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                        الموديل
                      </label>
                      <input
                        type="text"
                        placeholder="مثال: C200, X5"
                        value={formData.carModel}
                        onChange={(e) => setFormData({ ...formData, carModel: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                        سنة الصنع
                      </label>
                      <input
                        type="text"
                        placeholder="مثال: 2020"
                        value={formData.carYear}
                        onChange={(e) => setFormData({ ...formData, carYear: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      التاريخ المفضل للاستشارة
                    </label>
                    <input
                      type="date"
                      value={formData.bookingDate}
                      onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-right flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      الوقت المفضل
                    </label>
                    <input
                      type="time"
                      value={formData.bookingTime}
                      onChange={(e) => setFormData({ ...formData, bookingTime: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    ملاحظات إضافية أو وصف للتعديل المطلوب
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="4"
                    placeholder="أخبرنا عن التعديلات التي تود إجراؤها على سيارتك..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white font-bold py-4 rounded-lg transition-all disabled:opacity-50 shadow-lg"
                >
                  {submitting ? 'جاري إرسال الطلب...' : 'تأكيد حجز الاستشارة المجانية'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModificationBooking;


